import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import {useCallback, useMemo, useState} from 'react'
import {getFindingSeverity, getFindingSource, isNeedsAttention, SEVERITY_ORDER} from './entity-helpers'
import type {Finding} from './types'

export type FindingsView = 'attention' | 'open' | 'resolved'

export const useFilterFindings = (openFindings: Finding[], ignoredFindings: Finding[]) => {
  const [selectedView, setSelectedView] = useState<FindingsView>('attention')
  const [selectedSeverities, setSelectedSeverities] = useState<Set<RuleSeverity>>(() => new Set())
  const [selectedSources, setSelectedSources] = useState<Set<string>>(() => new Set())

  const allFindings = useMemo(() => [...openFindings, ...ignoredFindings], [openFindings, ignoredFindings])

  const availableSeverities = useMemo(() => {
    const severities = new Set<RuleSeverity>()
    for (const f of allFindings) {
      const s = getFindingSeverity(f)
      if (s) severities.add(s)
    }
    return SEVERITY_ORDER.filter(s => severities.has(s))
  }, [allFindings])

  const availableSources = useMemo(() => {
    const sources = new Set<string>()
    for (const f of allFindings) {
      sources.add(getFindingSource(f))
    }
    return [...sources].sort()
  }, [allFindings])

  const handleSeverityToggle = useCallback((severity: RuleSeverity) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev)
      if (next.has(severity)) next.delete(severity)
      else next.add(severity)
      return next
    })
  }, [])

  const handleSourceToggle = useCallback((source: string) => {
    setSelectedSources(prev => {
      const next = new Set(prev)
      if (next.has(source)) next.delete(source)
      else next.add(source)
      return next
    })
  }, [])

  const handleClearFilters = useCallback(() => {
    setSelectedSeverities(new Set())
    setSelectedSources(new Set())
  }, [])

  const applyFilters = useCallback(
    (items: Finding[]) => {
      return items.filter(f => {
        if (selectedView === 'attention' && !isNeedsAttention(f)) return false
        if (selectedView === 'open' && isNeedsAttention(f)) return false
        if (selectedSeverities.size > 0) {
          const s = getFindingSeverity(f)
          if (!s || !selectedSeverities.has(s)) return false
        }
        if (selectedSources.size > 0) {
          if (!selectedSources.has(getFindingSource(f))) return false
        }
        return true
      })
    },
    [selectedView, selectedSeverities, selectedSources],
  )

  const attentionCount = useMemo(() => openFindings.filter(isNeedsAttention).length, [openFindings])

  const displayedFindings = useMemo(
    () => applyFilters(selectedView === 'resolved' ? ignoredFindings : openFindings),
    [applyFilters, selectedView, openFindings, ignoredFindings],
  )

  return {
    selectedView,
    handleViewChange: setSelectedView,
    attentionCount,
    availableSeverities,
    availableSources,
    selectedSeverities,
    selectedSources,
    handleSeverityToggle,
    handleSourceToggle,
    handleClearFilters,
    displayedFindings,
  }
}
